<template>
  <div :class="`d-flex gap-1 justify-content-${justifyContentClass}`">
    <router-link
      v-if="showBackButton && backRoute"
      :to="backRoute"
      class="button-with-outline py-2 px-3"
      :title="$t('Back')"
    >
      <i :class="backIcon" />
      {{ backText }}
    </router-link>
    <button
      v-else-if="showBackButton && backAction"
      type="button"
      class="button-with-outline py-2 px-3"
      :title="backText"
      @click.prevent.stop="onBack"
    >
      <i :class="backIcon" />
      {{ backText }}
    </button>

    <button
      type="button"
      :class="submitButtonClasses"
      :disabled="requestInProgress || disabled"
      @click.prevent.stop="onSubmit"
    >
      <i v-if="!requestInProgress" :class="submitIcon" />

      <b-spinner
        v-else
        :title="$t('Request in progress, please try again later.')"
        style="height: 12px; width: 12px"
      />

      {{ submitText }}
    </button>
  </div>
</template>

<script>
import { mapMutations } from "vuex";

export default {
  name: "FormActions",

  props: {
    submitAction: {
      type: Function,
      required: true,
    },

    backAction: {
      type: Function,
      default: null,
    },

    isEdit: {
      type: Boolean,
      default: false,
    },

    // support for vue query mutation
    isPending: {
      type: [Boolean, null],
      default: null,
    },

    showBackButton: {
      type: Boolean,
      default: true,
    },

    backRoute: {
      type: [String, Object],
      default: () => null,
    },

    customSubmitText: {
      type: String,
      default: null,
    },

    customSubmitIcon: {
      type: String,
      default: null,
    },

    customBackText: {
      type: String,
      default: null,
      required: false,
    },

    customBackIcon: {
      type: String,
      default: "fa fa-arrow-left",
    },

    justifyContentClass: {
      type: String,
      default: "between",
    },

    variant: {
      type: String,
      default: "form", // 'form' or 'modal'
      validator: (value) => ["form", "modal"].includes(value),
    },

    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data() {
    return {
      inProgress: false,
    };
  },

  computed: {
    submitButtonClasses() {
      const classes = ["button-with-outline", "py-2 px-3"];

      if (this.variant === "form" && this.showBackButton) {
        classes.push("ms-auto");
      }

      return classes.join(" ");
    },

    // we support external mutation ( via isPending prop ) or internal loading status
    // ( for legacy request from strore ) via inProgress data value
    requestInProgress: {
      set: function (value) {
        if (this.isPending !== null) {
          return;
        }
        this.inProgress = value;
      },
      get: function () {
        if (this.isPending !== null) {
          return this.isPending;
        }
        return this.inProgress;
      },
    },
    submitIcon() {
      if (this.customSubmitIcon) {
        return `fa ${this.customSubmitIcon}`;
      }

      return this.isEdit ? "fa fa-save" : "fa fa-plus";
    },

    submitText() {
      if (this.customSubmitText) {
        return this.$t(this.customSubmitText);
      }

      return this.isEdit ? this.$t("Save") : this.$t("Create");
    },

    backIcon() {
      if (this.customBackIcon) {
        return `fa ${this.customBackIcon}`;
      }

      return "fa fa-arrow-left";
    },

    backText() {
      if (this.customBackText) {
        return this.$t(this.customBackText);
      }

      return this.$t("Back");
    },
  },

  methods: {
    ...mapMutations({
      showNotification: "notifications/showNotification",
    }),

    async onSubmit() {
      if (this.requestInProgress) {
        this.showNotification({
          type: "info",
          text: this.$t("Request in progress, please try again later."),
        });

        return;
      }

      const form = this.$el.closest("form");
      if (form && !form.reportValidity()) {
        return;
      }

      try {
        this.requestInProgress = true;

        // Clear unsaved changes flag before submitting so that any navigation
        // triggered inside submitAction() is not blocked by the router guard.
        this.$store.dispatch("setHasUnsavedChanges", false);

        await this.submitAction();
      } finally {
        this.requestInProgress = false;
      }
    },

    onBack() {
      if (this.backAction) {
        this.backAction();
      }
    },
  },
};
</script>
